import { useCallback, useMemo, useState } from "react";
import { readReceipt, writeReceipt } from "./receipt";
import {
  acceptAll,
  rejectAll,
  type ConsentCategory,
  type ConsentDecision,
  type ConsentReceipt,
} from "./types";

/**
 * The server-receipt sink (Tech decisions): the banner posts a `ConsentEvent(COOKIE_CHOICE)` server
 * side carrying only the granted categories — NO IP/fingerprint. The host app supplies this (wired to
 * `POST /api/public/v1/venues/{id}/cookie-consent`), so `@repo/consent` never imports an HTTP client.
 */
export type RecordServerReceipt = (grantedCategories: ConsentCategory[]) => void | Promise<void>;

export interface UseCookieConsentOptions {
  /** The current privacy-notice version — a change re-prompts (Tech decisions). */
  policyVersion: string;
  /** Optional server-receipt sink; called with the granted categories on every save. */
  onRecord?: RecordServerReceipt;
  /** Injectable clock for tests. */
  now?: () => number;
}

export interface UseCookieConsent {
  /** The stored decision, or null when the banner must show (no valid receipt). */
  receipt: ConsentReceipt | null;
  /** True while no valid receipt exists — the banner should be visible (R1). */
  needsChoice: boolean;
  /** Whether a specific category is currently granted (strictly-necessary is always true). */
  isGranted: (category: ConsentCategory) => boolean;
  /** Accept every category. */
  acceptAll: () => void;
  /** Refuse every optional category (strictly-necessary stays on) — the reject-all path (R1). */
  rejectAll: () => void;
  /** Save an explicit per-category decision (the "customise" path). */
  save: (decision: ConsentDecision) => void;
  /** Grant exactly ONE category (the placeholder-click path of `ConsentGate`, R2) — others untouched. */
  grantOne: (category: ConsentCategory) => void;
}

/**
 * The cookie-consent state hook (features/privacy-and-consent.md R1/R2). It reads the localStorage
 * receipt (re-prompting after 6 months or a policy change), exposes per-category grants, and on every
 * save writes the receipt AND calls {@link UseCookieConsentOptions.onRecord} with the granted set. A
 * placeholder click grants a single category without accepting the rest (R2, the OSM-map case).
 */
export function useCookieConsent(options: UseCookieConsentOptions): UseCookieConsent {
  const { policyVersion, onRecord, now = Date.now } = options;
  const [receipt, setReceipt] = useState<ConsentReceipt | null>(() =>
    readReceipt(policyVersion, now()),
  );

  const persist = useCallback(
    (decision: ConsentDecision) => {
      const stored = writeReceipt(decision, policyVersion, now());
      setReceipt(stored);
      const granted = (Object.keys(decision) as ConsentCategory[]).filter((c) => decision[c]);
      void onRecord?.(granted);
    },
    [policyVersion, onRecord, now],
  );

  const grantOne = useCallback(
    (category: ConsentCategory) => {
      const base = receipt?.decision ?? rejectAll();
      persist({ ...base, [category]: true });
    },
    [receipt, persist],
  );

  const isGranted = useCallback(
    (category: ConsentCategory) => receipt?.decision[category] ?? category === "strictlyNecessary",
    [receipt],
  );

  return useMemo(
    () => ({
      receipt,
      needsChoice: receipt === null,
      isGranted,
      acceptAll: () => persist(acceptAll()),
      rejectAll: () => persist(rejectAll()),
      save: (decision: ConsentDecision) => persist(decision),
      grantOne,
    }),
    [receipt, isGranted, persist, grantOne],
  );
}
